import { renderRosterPage, type RosterFetch, type RosterPageSpec, type Navigate } from './roster-page.js';
import type { ActionFetch } from './roster-actions.js';

/**
 * CAOS-1736 — Installed Packages as a PLATFORM page, so an org can always remove a package.
 *
 * ── THE DEFECT, WHICH IS NOT ABOUT A FIXTURE ────────────────────────────────────────────────
 * Install a third-party workspace that masters the shell — reproduced on 2026-10-19 in
 * `caos1505reg` with one of the CAOS-1504 fixtures — and the org is left with no way to manage
 * packages from any screen, including no way to remove the workspace just installed.
 *
 * The reason is structural rather than incidental. The Installed Packages screen belonged to
 * `caos-pkg-setup` (`src/rosterPages/installed_packages_list.json`, claimed into the `platform`
 * group), and a third-party workspace that takes the shell slot does not contain that package and
 * does not depend on it. There is no combination that gives you both: the only other picker entry
 * is the standard bundle, whose `pkg_core_shell` claims the shell slot and is NOT `replaceable`,
 * which is the very property that makes the fixtures the honest test of an open slot. So an org
 * could have a third-party workspace, or a way to remove packages, and not both.
 *
 * That would apply to any real third-party workspace a customer installed. CAOS-881's premise is
 * that the workspace slot is open to anyone; on the evidence it was open to anyone who never needed
 * to change their mind.
 *
 * ── WHY THE PAGE LIVES HERE AND NOT IN A PACKAGE ────────────────────────────────────────────
 * Direction 1 of the two the ticket recorded, decided on the ticket before this was written: package
 * management belongs to the platform, not to a package. It has an incumbent rather than being a new
 * idea — Domains, Seam Order and Access Explain are already platform-core Setup pages, present
 * whichever workspace holds the shell slot, and Installed Packages was the outlier.
 *
 * The rejected alternative was requiring a shell master to provide or depend on a package-management
 * surface. That makes the right to change your mind a property of the publisher's goodwill, and
 * would have to be enforced at deploy against every future third-party workspace.
 *
 * ── IT IS A SPEC, NOT AUTHORED METADATA, AND THAT IS THE WHOLE POINT ────────────────────────
 * Every other Setup browse this host draws is authored as a `rosterPage` component in a package and
 * resolved by name. This one CANNOT be: the defect is that the org may hold no such package. A page
 * that must exist when nothing is installed cannot be delivered by something installed.
 *
 * So the host declares the spec and hands it to the SAME generic renderer
 * ({@link renderRosterPage}) every authored roster goes through. No new rendering primitive, and no
 * bespoke screen: the search, the sort, the confirm dialog, the uninstall preview and its cascade
 * retry are the shared implementation, not a second one written here.
 *
 * ── THE PLATFORM ANSWERS THIS ROUTE, AND A PACKAGE CANNOT TAKE IT ───────────────────────────
 * The route is reserved: `setup-surface.ts` answers `installed-packages` above its contributed-page
 * lookup, and `allSectionGroups` drops a contributed claim on that key. So `caos-pkg-setup` keeps
 * its authored page in the package, but this is what draws in every org.
 *
 * The first version did the opposite — it sat BELOW the lookup so the package's page would answer
 * first, on the reasoning that zero regression beats zero duplication. A review of #908 showed that
 * reasoning does not survive contact with the threat model this ticket is about. A `setup` inject
 * claim needs only its own `label` to be drawn and its `page` need not resolve to anything
 * (CAOS-762), and deploy validation refuses a route collision only WITHIN one component, never
 * across them (`kernel/src/capabilities/validate-capabilities.ts` rule 8, which says so explicitly:
 * "one package's install never fails because of another package's choices"). So any installed
 * package could claim this route, suppress the platform's rail entry, intercept the address and
 * leave the org on "Page not found" with no way to remove a package. A third-party workspace
 * mastering the shell is the package most likely to ship a Setup claim, and it is the whole subject
 * of this ticket — the defect would have been reachable by the very org the fix was written for.
 *
 * Reserving costs that org nothing measurable, which is what makes the trade easy rather than
 * merely necessary. The spec below was MIRRORED from `installed_packages_list.json` field for field
 * — verified against the file rather than assumed — so the screen an org with the package stops
 * drawing and the screen it starts drawing are the same screen.
 *
 * CAOS-1827 took the mirror out of lock-step, deliberately, and it is now a SUPERSET rather than a
 * copy. Three licensed differences, and no others:
 *
 *   • `key`, which names where the spec came from so a reader of the DOM can tell them apart;
 *   • `cardFields`, added here because the default view answered none of the four facts the
 *     description promises;
 *   • `description`, narrowed in the same change so it stops promising a fact the card cannot show.
 *
 * The package's copy is NOT updated to match, and that is a decision rather than an oversight: as
 * `caos-pkg-setup` declares it today nothing reaches that page, so changing it would alter no
 * screen, while a package body edit reddens every open pull request in this repo until the standard
 * registry is regenerated. A divergence nobody can see costs less than that. If the page is ever
 * retired as the next step below suggests, this list stops needing to be kept at all.
 *
 * "Nothing reaches it" is a fact about the CURRENT manifest, not a guarantee — flagged by the same
 * review. The host answers `installed-packages` before the contributed-page lookup, and the
 * reserved-key rule strips the rail entry claiming that exact route; a claim pointing `page:
 * installed_packages_list` at some OTHER route would still be served by the generic contributed
 * page. `caos-1736-installed-packages-is-platform-core.test.ts` pins that the shipped claim uses
 * the matching route, which is what makes the statement true in practice.
 *
 * Retiring the package's page outright is a cross-repo change — the package, plus a regeneration of
 * `standard-registry.generated.ts` against a full set of package checkouts, plus two merges
 * sequenced — and this ticket is a High-priority defect about an org with no way out. It is the
 * named next step, and it is now only a tidy-up: nothing reads that page any more.
 */

/** The route segment, and the section key the rail files this page under. */
export const INSTALLED_PACKAGES_ROUTE = 'installed-packages';

/**
 * The platform's Installed Packages roster.
 *
 * Mirrored from `caos-pkg-setup/src/rosterPages/installed_packages_list.json`. Where a line here
 * looks arbitrary it is almost certainly carried over from that file on purpose. Since CAOS-1827 it
 * is a superset rather than a copy: it differs in `key`, `cardFields` and `description`, each
 * annotated at its own line, and in nothing else.
 */
export const INSTALLED_PACKAGES_SPEC: RosterPageSpec = {
  // Deliberately NOT `installed_packages_list`. That is the authored component's api name, and two
  // different things answering to one name is how a reader stops being able to tell them apart.
  key: 'platform_installed_packages',
  label: 'Installed Packages',
  eyebrow: 'Setup',
  description:
    // CAOS-1827 — this used to promise four facts flatly, and the DEFAULT view answered one of
    // them. A card is deliberately compact — a name, a sub-line and at most two facts, which is the
    // restraint that stopped an earlier card dumping every field — so a screen with three views
    // cannot promise all four in all three. The two the card now carries are below; the namespace is
    // the one it does not, and the sentence says where to find it rather than implying a card shows it.
    'Every package installed in this org — what it is, the version live right now, and whether an admin may change what it brought. The table and split views add the namespace its members carry.',
  read: { noun: 'packages', verb: 'list' },
  rowsAt: 'packages',
  idField: 'name',
  titleField: 'label',
  subtitleField: 'name',
  /**
   * CAOS-1827 — the two facts a CARD carries, apart from the table columns. Without these a card
   * was the package label over its api name and nothing else, so the default view was the one view
   * that did not show the version — the single most load-bearing fact here, because "which version
   * of this package is the org running" is what decides whether a missing screen is a missing
   * feature or an old install. The data was never absent: the table and split views both drew it
   * from the same row, and only `card` has no cells to draw it in.
   *
   * Version first because of the above. Manageability second rather than the namespace, because it
   * is what tells an admin whether they may change anything the package brought, and it is the fact
   * the Remove action below reads against.
   *
   * TWO and not three, and the cap is a CONVENTION rather than a guard — measured, after a review
   * caught this comment claiming otherwise. `caos-ui/src/components/card-grid.ts:72` types
   * `metrics` as an open-ended `CaosCardMetric[]` and documents it as "up to two", and the render
   * loop iterates every entry without slicing, so a third WOULD draw. What two protects is the
   * restraint itself: a compact card is the standard the component's own doc sets, and the earlier
   * card that dumped every field onto itself is what the standard exists to prevent. Nothing stops
   * this spec from breaking it, so the assertion in
   * `host/test/caos-1827-the-default-view-shows-the-version.test.ts` is what holds it — not the
   * component.
   */
  cardFields: [
    { label: 'Version', valueAt: 'version' },
    { label: 'Manageability', valueAt: 'manageability' },
  ],
  columns: [
    { key: 'label', label: 'Package', valueAt: 'label' },
    { key: 'name', label: 'API Name', valueAt: 'name' },
    { key: 'version', label: 'Version', valueAt: 'version' },
    { key: 'namespace', label: 'Namespace', valueAt: 'namespace' },
    // `manageability` and `installedAt` are projected by `describePackageInstalls` — checked in the
    // kernel rather than assumed, because a column over a path the route does not carry draws blank.
    { key: 'manageability', label: 'Manageability', valueAt: 'manageability' },
    { key: 'installedAt', label: 'Installed', valueAt: 'installedAt' },
  ],
  searchPlaceholder: 'Search installed packages by name or namespace…',
  searchFields: ['label', 'name', 'namespace'],
  emptyLabel: 'No packages are installed in this org yet.',
  searchEmptyLabel: 'No installed package matches "{search}".',
  sortOptions: [
    { value: 'label:asc', label: 'Package (A–Z)' },
    { value: 'label:desc', label: 'Package (Z–A)' },
    { value: 'installedAt:desc', label: 'Most recently installed' },
    { value: 'installedAt:asc', label: 'Longest installed' },
  ],
  rowIdField: 'name',
  /**
   * The row action this whole ticket exists for: removing a package from the screen.
   *
   * `preview` is not decoration. A `confirmText` is written when the page is authored and cannot
   * know the real question — "removing this ALSO removes these three, still yes?" — so CAOS-1502
   * made the confirm dialog ask a read-only route first. An uninstall that cascades is exactly the
   * case it was built for, and a workspace other packages were installed against is exactly the
   * package most likely to cascade.
   */
  rowAction: {
    key: 'remove_this_package',
    label: 'Remove',
    kind: 'confirm',
    style: 'danger',
    verb: { noun: 'packages', verb: 'uninstall' },
    params: { packageKey: '{rowId}' },
    confirmText:
      'Remove this package from the org? Objects it created and the records in them are kept.',
    preview: {
      verb: { noun: 'packages', verb: 'uninstall-dry-run' },
      consequenceAt: 'cascaded',
      consequenceLabel: 'Removing it also removes {count} packages that depend on it:',
      emptyLabel:
        'Nothing else is removed with it. Objects it created and the records in them are kept.',
    },
    cascade: { param: 'withDependents', label: 'Remove those packages too' },
    successMessage:
      'The package and everything it contributed have been removed. Objects it created and the records in them are kept.',
  },
  pageActions: [
    {
      key: 'remove_package',
      label: 'Remove a package',
      kind: 'form',
      style: 'danger',
      verb: { noun: 'packages', verb: 'uninstall' },
      fields: [
        {
          key: 'packageKey',
          label: 'Package to remove',
          type: 'select',
          required: true,
          optionsFrom: {
            read: { noun: 'packages', verb: 'list' },
            rowsAt: 'packages',
            valueAt: 'name',
            labelAt: 'label',
          },
        },
        {
          key: 'withDependents',
          label: 'If other packages depend on it',
          type: 'select',
          required: true,
          options: [
            { value: 'false', label: 'Stop and tell me which ones' },
            { value: 'true', label: 'Remove those packages too' },
          ],
        },
      ],
      successMessage:
        'The package and everything it contributed have been removed. Objects it created and the records in them are kept.',
    },
  ],
};

let installedPackagesFetch: RosterFetch | undefined;
let installedPackagesActionFetch: ActionFetch | undefined;

/**
 * Override the reads this page makes (tests only).
 *
 * The same seam `setSetupComponentLibraryFetch` and `setSetupHomePackagesFetch` already provide, and
 * it exists for a measured reason rather than for symmetry. `renderMain` constructs this page with
 * no fetch of its own, so it falls to `defaultRosterFetch` — the real `fetch`. A test driving the
 * page through `renderSetupSurface` therefore has only `globalThis.fetch` to reach for, and
 * replacing that one breaks Vitest's own module transport: the first version of this suite did
 * exactly that in a `beforeEach` and the whole FILE failed to load, reporting "0 test" rather than a
 * failure. A suite that does not load subtracts silently, which is the worst shape a red can take.
 */
export function setSetupInstalledPackagesFetch(read?: RosterFetch, action?: ActionFetch): void {
  installedPackagesFetch = read;
  installedPackagesActionFetch = action;
}

/**
 * Draw the platform's Installed Packages page.
 *
 * A thin bridge, the same shape `setup-domains.ts` and `seam-order.ts` are: it names the route and
 * hands the spec to the shared renderer. The `done` promise is returned so a test can await the
 * first read; `renderMain` takes `.root` and drops it, exactly as it does for every other bridge.
 */
export function renderSetupInstalledPackages(ctx: {
  readonly navigate?: Navigate;
  readonly fetch?: RosterFetch;
  readonly actionFetch?: ActionFetch;
}): { root: HTMLElement; done: Promise<void> } {
  const read = ctx.fetch ?? installedPackagesFetch;
  const action = ctx.actionFetch ?? installedPackagesActionFetch;
  return renderRosterPage(INSTALLED_PACKAGES_SPEC, {
    ...(ctx.navigate ? { navigate: ctx.navigate } : {}),
    ...(read ? { fetch: read } : {}),
    ...(action ? { actionFetch: action } : {}),
  });
}
